import { Action } from 'redux';
import { AppUrls } from '../../app/AppUrls';
import { ContentType } from '../models/ContentType';
import { ContentTypeFilterValue } from '../models/ContentTypeFilterValue';
import { MixedContentTypeSearchResult, SearchResultItem, SearchResultList } from '../models/SearchResultTypes';
import { DataPointsById, FailuresById } from '../models/IMetricDataList';
import { SortSelection } from '../models/SortSelection';
import { FilterTypes } from '../models/FilterTypes';
import { DisplaySelection } from '../models/DisplaySelection';

export enum SearchActionTypes {
  CHANGE_CONTENT_TYPE = 'SEARCH_ACTION/CHANGE_CONTENT_TYPE',
  CHANGE_CONTENT_BY_URL_PATH = 'SEARCH_ACTION/CHANGE_CONTENT_BY_URL_PATH',
  PERFORM_SEARCH = 'SEARCH_ACTION/PERFORM_SEARCH',
  SEARCH_SUCCESS = 'SEARCH_ACTION/SEARCH_SUCCESS',
  SEARCH_SUCCESS_WITH_METADATA = 'SEARCH_ACTION/SEARCH_SUCCESS_WITH_METADATA',
  SEARCH_FAILED = 'SEARCH_ACTION/SEARCH_FAILED',
  ORDER_BY = 'SEARCH_ACTION/ORDER_BY',
  LOAD_INITIAL_DISPLAY_MODE = 'SEARCH_ACTION/LOAD_INITIAL_DISPLAY_MODE',
  SET_DISPLAY_MODE = 'SEARCH ACTION/SET_DISPLAY_MODE',
  ADD_CONTENT_FAVORITE_ACTION = 'SEARCH ACTION/ADD_CONTENT_FAVORITE_ACTION',
  REMOVE_CONTENT_FAVORITE_ACTION = 'SEARCH ACTION/REMOVE_CONTENT_FAVORITE_ACTION',
  CONTENT_FAVORITE_ADDED_ACTION = 'SEARCH ACTION/CONTENT_FAVORITE_ADDED_ACTION',
  CONTENT_FAVORITE_REMOVED_ACTION = 'SEARCH ACTION/CONTENT_FAVORITE_REMOVED_ACTION',
}

export interface PerformSearchSucceededAction {
  readonly type: SearchActionTypes.SEARCH_SUCCESS;
  readonly results: MixedContentTypeSearchResult;
}

export interface PerformSearchSucceededWithMetadataAction {
  readonly type: SearchActionTypes.SEARCH_SUCCESS_WITH_METADATA;
  readonly contentType: ContentType;
  readonly data: SearchResultItem[];
}

export interface PerformSearchFailedAction {
  readonly type: SearchActionTypes.SEARCH_FAILED;
  readonly error: string;
}

export interface PerformSearchAction {
  readonly type: SearchActionTypes.PERFORM_SEARCH;
  readonly searchTerm: string;
}

export interface ChangeContentByUrlPathAction {
  readonly type: SearchActionTypes.CHANGE_CONTENT_BY_URL_PATH;
  readonly searchTerm: string;
  readonly urlPath: string;
  readonly urls: AppUrls;
  readonly filters: string;
}

export interface ChangeContentTypeAction {
  readonly type: SearchActionTypes.CHANGE_CONTENT_TYPE;
  readonly contentTypeFilterValue: ContentTypeFilterValue;
}

export interface OrderByAction {
  readonly type: SearchActionTypes.ORDER_BY;
  readonly sortSelection: SortSelection;
}

export interface SetDisplayModeAction {
  readonly type: SearchActionTypes.SET_DISPLAY_MODE;
  readonly displaySelection: DisplaySelection;
}

export interface LoadInitialDisplayAction {
  readonly type: SearchActionTypes.LOAD_INITIAL_DISPLAY_MODE;
  readonly displaySelection: DisplaySelection;
}

export interface AddContentFavoriteAction {
  readonly type: SearchActionTypes.ADD_CONTENT_FAVORITE_ACTION;
  readonly item: SearchResultItem;
}

export interface RemoveContentFavoriteAction {
  readonly type: SearchActionTypes.REMOVE_CONTENT_FAVORITE_ACTION;
  readonly item: SearchResultItem;
}

export interface ContentFavoriteAddedAction {
  readonly type: SearchActionTypes.CONTENT_FAVORITE_ADDED_ACTION;
  readonly item: SearchResultItem;
}

export interface ContentFavoriteRemovedAction {
  readonly type: SearchActionTypes.CONTENT_FAVORITE_REMOVED_ACTION;
  readonly item: SearchResultItem;
}

export enum ContentActionTypes {
  REQUEST = 'CONTENT_ACTION/REQUEST',
  SUCCESS = 'CONTENT_ACTION/SUCCESS',
  SUCCESSWITHMETADATA = 'REQUEST_CONTENT_ACTION/SUCCESSWITHMETADATA',
}

export interface RequestContentSliceAction {
  readonly type: ContentActionTypes.REQUEST;
  readonly offset: number;
  readonly count: number;
  readonly contentType: ContentType;
}

export interface RequestContentSliceSucceededAction {
  readonly type: ContentActionTypes.SUCCESS;
  readonly data: SearchResultList<SearchResultItem>;
}

export interface RequestContentSliceSucceededWithMetadataAction {
  readonly type: ContentActionTypes.SUCCESSWITHMETADATA;
  readonly contentType: ContentType;
  readonly data: SearchResultItem[];
}

export enum FilterActionTypes {
  UPDATE_FILTER = 'FILTER_ACTION/UPDATE_FILTER',
}

export interface UpdateFilterAction {
  readonly type: FilterActionTypes.UPDATE_FILTER;
  readonly id: string;
  readonly value: string;
  readonly filterType: FilterTypes;
}

export enum MetricsDataActionTypes {
  REQUEST_START = 'METRICS_DATA/REQUEST_START',
  REQUEST_SUCCEEDED = 'METRICS_DATA/REQUEST_SUCCEEDED',
  REQUEST_FAILED = 'METRICS_DATA/REQUEST_FAILED',
  UPDATE_DATA = 'METRICS_DATA/UPDATE',
}

export interface MetricsDataRequestStartAction extends Action {
  type: MetricsDataActionTypes.REQUEST_START;
  ids: ReadonlyArray<string>;
}

export interface MetricsDataRequestSucceededAction extends Action {
  type: MetricsDataActionTypes.REQUEST_SUCCEEDED;
  dataPointsById: DataPointsById;
}

export interface MetricsDataRequestFailedAction extends Action {
  type: MetricsDataActionTypes.REQUEST_FAILED;
  failuresById: FailuresById;
}

export interface UpdateMetricsDataAction extends Action {
  type: typeof MetricsDataActionTypes.UPDATE_DATA;
  metrics: string[];
}

export type SearchActions =
  | PerformSearchSucceededAction
  | PerformSearchSucceededWithMetadataAction
  | PerformSearchFailedAction
  | PerformSearchAction
  | ChangeContentByUrlPathAction
  | ChangeContentTypeAction
  | OrderByAction
  | SetDisplayModeAction
  | LoadInitialDisplayAction
  | AddContentFavoriteAction
  | RemoveContentFavoriteAction
  | ContentFavoriteAddedAction
  | ContentFavoriteRemovedAction;

export type SingleContentTypeActions =
  | RequestContentSliceSucceededAction
  | RequestContentSliceSucceededWithMetadataAction
  | RequestContentSliceAction
  | ChangeContentTypeAction
  | PerformSearchSucceededAction
  | ChangeContentByUrlPathAction
  | UpdateFilterAction
  | ContentFavoriteAddedAction
  | ContentFavoriteRemovedAction;

export type FilterActions = UpdateFilterAction | ChangeContentByUrlPathAction | PerformSearchSucceededAction | PerformSearchFailedAction;

export type MetricsDataAction = MetricsDataRequestStartAction | MetricsDataRequestSucceededAction | MetricsDataRequestFailedAction;
